import { useId, useState } from 'react';
import { Form, Button, InputGroup, Nav } from 'react-bootstrap';
import { Search, X } from 'react-bootstrap-icons';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { useClientFlags } from '@/hooks/useClientFlags';
import { groupClientsByType } from '@/services/clientService';
import type { Client } from '@/types';
import './clientPicker.css';

interface GroupedClientSelectorProps {
  clients: Client[];
  selectedClientNames: string[];
  onClientToggle: (clientName: string) => void;
  onSelectClients: (clientNames: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

type CustomerScope = 'active' | 'all';

/** How many chosen clients are named under the list before the rest fold into a count. */
const CHIPS_SHOWN = 5;

/**
 * Pick many clients. The list is split into dev stacks and customer stacks,
 * each group can be ticked as a whole, and one client can still be ticked or
 * cleared on its own at any time.
 *
 * Customer stacks marked inactive are left out until "All customers" is
 * chosen, so a group tick never picks one up by accident. A client that is
 * already ticked stays in the list whatever the filter says. The tags come
 * from the shared tag reader, so a caller passes nothing for them.
 */
export function GroupedClientSelector({
  clients,
  selectedClientNames,
  onClientToggle,
  onSelectClients,
  disabled = false,
  loading = false,
}: GroupedClientSelectorProps) {
  const flags = useClientFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const [scope, setScope] = useState<CustomerScope>('active');
  // Two pickers can sit on one page, so each gets its own ids.
  const uid = useId();

  const { devClients, productionClients } = groupClientsByType(clients);
  const selected = new Set(selectedClientNames);

  // The customer stacks in the list: the active ones, plus any inactive one that is ticked.
  const activeCustomers = productionClients.filter((c) => !flags.isInactive(c.name));
  const listedCustomers =
    scope === 'all'
      ? productionClients
      : productionClients.filter((c) => !flags.isInactive(c.name) || selected.has(c.name));
  const listedClients = [...devClients, ...listedCustomers];
  const hiddenInactive = productionClients.length - listedCustomers.length;

  const lowerSearch = searchTerm.toLowerCase();
  const applySearch = (list: Client[]) =>
    searchTerm ? list.filter((c) => c.name.toLowerCase().includes(lowerSearch)) : list;
  const filteredDevClients = applySearch(devClients);
  const filteredProductionClients = applySearch(listedCustomers);
  // Inactive customers the search matches while they are filtered out. The empty list offers to show them.
  const hiddenMatches =
    scope === 'active' && searchTerm
      ? applySearch(productionClients).filter((c) => flags.isInactive(c.name) && !selected.has(c.name)).length
      : 0;

  const allInternalSelected = devClients.length > 0 && devClients.every((c) => selected.has(c.name));
  const someInternalSelected = devClients.some((c) => selected.has(c.name));

  const allClientsSelected = listedCustomers.length > 0 && listedCustomers.every((c) => selected.has(c.name));
  const someClientsSelected = listedCustomers.some((c) => selected.has(c.name));

  const allSelected = listedClients.length > 0 && listedClients.every((c) => selected.has(c.name));
  const isAllIndeterminate = selectedClientNames.length > 0 && !allSelected;

  // Each group tick acts on the clients in the list. A customer stack that is
  // filtered out as inactive is never picked up by one.
  const handleSelectAllToggle = () => {
    if (allSelected) {
      onSelectClients([]);
    } else {
      onSelectClients(listedClients.map((c) => c.name));
    }
  };

  const handleInternalToggle = () => {
    if (allInternalSelected) {
      // Drop every dev stack from the selection.
      const internalNames = new Set(devClients.map((c) => c.name));
      onSelectClients(selectedClientNames.filter((name) => !internalNames.has(name)));
    } else {
      // Add every dev stack to the selection.
      const newSelection = new Set(selectedClientNames);
      devClients.forEach((c) => newSelection.add(c.name));
      onSelectClients(Array.from(newSelection));
    }
  };

  const handleClientsToggle = () => {
    if (allClientsSelected) {
      // Drop every customer stack from the selection.
      const clientNames = new Set(productionClients.map((c) => c.name));
      onSelectClients(selectedClientNames.filter((name) => !clientNames.has(name)));
    } else {
      // Add every listed customer stack to the selection.
      const newSelection = new Set(selectedClientNames);
      listedCustomers.forEach((c) => newSelection.add(c.name));
      onSelectClients(Array.from(newSelection));
    }
  };

  const handleUnselectAll = () => {
    onSelectClients([]);
  };

  // A group with some of its clients ticked shows a dash, not a tick.
  const isInternalIndeterminate = someInternalSelected && !allInternalSelected;
  const isClientsIndeterminate = someClientsSelected && !allClientsSelected;

  const locked = disabled || loading;
  const nothingToList = filteredDevClients.length === 0 && filteredProductionClients.length === 0;

  const renderClient = (client: Client) => (
    <div className="client-pick__item" key={client.name}>
      <Form.Check
        type="checkbox"
        id={`${uid}-client-${client.name}`}
        label={
          <span className="client-pick__item-label">
            <span className="client-pick__item-name">{client.name}</span>
            <ClientTags clientName={client.name} flags={flags} />
          </span>
        }
        checked={selected.has(client.name)}
        onChange={() => onClientToggle(client.name)}
        disabled={locked}
      />
    </div>
  );

  return (
    <div className="client-pick">
      <div className="client-pick__groups">
        <span className="client-pick__groups-label">Show</span>
        <Nav
          variant="pills"
          className="nav-pills-custom client-pick__scope"
          activeKey={scope}
          onSelect={(key) => key && setScope(key as CustomerScope)}
          aria-label="Which customer stacks to list"
        >
          <Nav.Item>
            <Nav.Link eventKey="active" disabled={locked}>
              Active customers<span className="client-pick__count">{activeCustomers.length}</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="all" disabled={locked}>
              All customers<span className="client-pick__count">{productionClients.length}</span>
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      <div className="client-pick__groups">
        <Form.Check
          type="checkbox"
          id={`${uid}-all`}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = isAllIndeterminate;
          }}
          label={
            <span>
              All clients<span className="client-pick__count">{listedClients.length}</span>
            </span>
          }
          checked={allSelected}
          onChange={handleSelectAllToggle}
          disabled={locked || listedClients.length === 0}
        />
        <Form.Check
          type="checkbox"
          id={`${uid}-dev`}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = isInternalIndeterminate;
          }}
          label={
            <span>
              Dev stacks<span className="client-pick__count">{devClients.length}</span>
            </span>
          }
          checked={allInternalSelected}
          onChange={handleInternalToggle}
          disabled={locked || devClients.length === 0}
        />
        <Form.Check
          type="checkbox"
          id={`${uid}-customer`}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = isClientsIndeterminate;
          }}
          label={
            <span>
              Customer stacks<span className="client-pick__count">{listedCustomers.length}</span>
            </span>
          }
          checked={allClientsSelected}
          onChange={handleClientsToggle}
          disabled={locked || listedCustomers.length === 0}
        />
        {selectedClientNames.length > 0 && (
          <Button
            size="sm"
            variant="outline-secondary"
            className="client-pick__clear"
            onClick={handleUnselectAll}
            disabled={locked}
          >
            Clear
          </Button>
        )}
      </div>

      <InputGroup size="sm" className="client-pick__search">
        <InputGroup.Text>
          <Search size={14} />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search clients"
          aria-label="Search clients"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            // Enter in a search box must not send the form the picker sits in.
            if (e.key === 'Enter') e.preventDefault();
          }}
          disabled={locked}
        />
        {searchTerm && (
          <Button
            variant="outline-secondary"
            onClick={() => setSearchTerm('')}
            disabled={locked}
            aria-label="Clear the search"
          >
            <X size={14} />
          </Button>
        )}
      </InputGroup>

      <div className="client-pick__list">
        {filteredDevClients.length > 0 && (
          <>
            <div className="client-pick__group-label">
              <span>Dev stacks</span>
              <span>{filteredDevClients.length}</span>
            </div>
            {filteredDevClients.map(renderClient)}
          </>
        )}

        {filteredProductionClients.length > 0 && (
          <>
            <div className="client-pick__group-label">
              <span>Customer stacks</span>
              <span>{filteredProductionClients.length}</span>
            </div>
            {filteredProductionClients.map(renderClient)}
          </>
        )}

        {nothingToList && (
          <div className="client-pick__empty">
            {loading ? (
              'Loading clients'
            ) : searchTerm ? (
              hiddenMatches > 0 ? (
                <>
                  No active client matches "{searchTerm}". {hiddenMatches}{' '}
                  {hiddenMatches === 1 ? 'inactive customer does' : 'inactive customers do'}.
                  <Button variant="link" size="sm" className="client-pick__show-all" onClick={() => setScope('all')}>
                    Search all customers
                  </Button>
                </>
              ) : (
                `No client matches "${searchTerm}".`
              )
            ) : (
              'There are no clients to pick from.'
            )}
          </div>
        )}
      </div>

      <div className="client-pick__chosen">
        <span>
          {selectedClientNames.length} of {listedClients.length} selected
        </span>
        {selectedClientNames.slice(0, CHIPS_SHOWN).map((name) => {
          const isInternal = devClients.some((c) => c.name === name);
          return (
            <button
              type="button"
              key={name}
              className="client-pick__chip"
              onClick={() => onClientToggle(name)}
              disabled={locked}
              title={`Remove ${name}`}
            >
              {name}
              {isInternal && <span className="client-pick__chip-tag">dev</span>}
              <X size={12} aria-hidden="true" />
            </button>
          );
        })}
        {selectedClientNames.length > CHIPS_SHOWN && (
          <span className="client-pick__more">and {selectedClientNames.length - CHIPS_SHOWN} more</span>
        )}
        {hiddenInactive > 0 && (
          <span className="client-pick__more">
            {hiddenInactive} inactive {hiddenInactive === 1 ? 'customer is' : 'customers are'} not listed.
          </span>
        )}
      </div>
    </div>
  );
}
